import React, { useState } from 'react';
import { ScrollView, StyleSheet, View, Linking, Platform } from 'react-native';
import { useTheme, FAB, Surface, DataTable, IconButton, Text, List, Divider, Button, Chip } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useWeeklyReport } from '../../../hooks/useWeeklyReport';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import { getTimeLogs } from '../../../api/apiService';
import EditDayEventsModal from '../../EditDayEventsModal';
import { WeekPickerModal } from '../../WeekPickerModal';
import { ReportWorkCompanies } from '../../ReportWorkCompanies';
import { isReportRecordEditingEnabled } from '../../../utils/featureToggles';

export const WeeklyDrivingReport: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { report, loading, error, fetchReport, getWeeklyDrivingReportPDF } = useWeeklyReport();
  const [editDate, setEditDate] = useState<string | null>(null);
  const [expandedDays, setExpandedDays] = useState<{ [key: string]: boolean }>({});
  const [editedByDate, setEditedByDate] = useState<Record<string, { anyEdited: boolean; sessionStartEdited: boolean; sessionEndEdited: boolean; driveEdited: boolean }>>({});
  const [weekPickerVisible, setWeekPickerVisible] = useState(false);

  if (loading) {
    return (
      <Surface style={[styles.loadingContainer, { backgroundColor: theme.colors.surface }]}>
        <Text variant="bodyLarge" style={{ color: theme.colors.onSurface }}>
          {t('loading')}
        </Text>
      </Surface>
    );
  }
  if (error) {
    return (
      <Surface style={[styles.loadingContainer, { backgroundColor: theme.colors.surface }]}>
        <Text variant="bodyLarge" style={{ color: theme.colors.error }}>
          {error}
        </Text>
      </Surface>
    );
  }
  if (!report) {
    return (
      <Surface style={[styles.loadingContainer, { backgroundColor: theme.colors.surface }]}>
        <Text variant="bodyLarge" style={{ color: theme.colors.onSurface }}>
          {t('noReport')}
        </Text>
      </Surface>
    );
  }

  const toggleDay = (date: string) => {
    setExpandedDays((prev) => ({ ...prev, [date]: !prev[date] }));
    const willExpand = !expandedDays[date];
    if (willExpand && !editedByDate[date]) {
      getTimeLogs()
        .then((resp) => {
          if (resp.ok) {
            const entries = (resp.data || []).filter((e) => {
              try {
                return new Date(e.timestamp).toISOString().slice(0, 10) === date;
              } catch {
                return false;
              }
            });
            const flags = {
              anyEdited: entries.some((e: any) => !!(e as any).edited),
              sessionStartEdited: entries.some((e: any) => e.event_type === 'session_in' && !!e.edited),
              sessionEndEdited: entries.some((e: any) => e.event_type === 'session_out' && !!e.edited),
              driveEdited: entries.some((e: any) => (e.event_type === 'drive_in' || e.event_type === 'drive_out') && !!e.edited),
            };
            setEditedByDate((prev) => ({ ...prev, [date]: flags }));
          }
        })
        .catch(() => {});
    }
  };


  const handleWeekSelection = (weekStart: string) => {
    fetchReport(weekStart);
  };

  const exportToPDF = async () => {
    try {
      // Service now returns Blob directly or throws on error
      const blob = await getWeeklyDrivingReportPDF(report.week_start);

      if (Platform.OS === 'web') {
        // Web-specific handling
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `weekly_driving_report_${report.week_start}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Mobile handling
        const fileUri = `${FileSystem.documentDirectory}weekly_driving_report_${report.week_start}.pdf`;
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result?.toString().split(',')[1] || '');
          reader.readAsDataURL(blob);
        });
        await FileSystem.writeAsStringAsync(fileUri, base64, { encoding: FileSystem.EncodingType.Base64 });
        await Sharing.shareAsync(fileUri);
      }
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
      alert(t('errorExportingPDF'));
    }
  };

  const openGoogleMaps = (location: string | null) => {
    if (location) {
      const pointMatch = location.match(/POINT\s*\((-?\d+\.\d+)\s+(-?\d+\.\d+)\)/);
      if (pointMatch) {
        const [_, lon, lat] = pointMatch;
        if (lat && lon) {
          const url = `https://www.google.com/maps?q=${lat},${lon}`;
          if (Platform.OS === 'web') {
            window.open(url, '_blank');
          } else {
            Linking.openURL(url).catch((err) => console.error('Erro ao abrir Google Maps:', err));
          }
        }
      } else {
        const [lat, lon] = location.split(',').map((coord) => coord.trim());
        if (lat && lon) {
          const url = `https://www.google.com/maps?q=${lat},${lon}`;
          if (Platform.OS === 'web') {
            window.open(url, '_blank');
          } else {
            Linking.openURL(url).catch((err) => console.error('Erro ao abrir Google Maps:', err));
          }
        }
      }
    }
  };

  return (
    <Surface style={[styles.container, { backgroundColor: theme.colors.surface }]}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Detalhes do Condutor */}
        <View style={styles.section}>
          <Text variant="titleLarge" style={{ color: theme.colors.primary, fontSize: 20, fontWeight: '600' }}>
            {t('driverDetails')}
          </Text>
          <Divider style={[styles.divider, { backgroundColor: theme.colors.outline }]} />
          <List.Item
            title={t('name')}
            description={report.driver_details.name}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('email')}
            description={report.driver_details.email}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('phoneNumber')}
            description={report.driver_details.phone_number || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('dateOfBirth')}
            description={report.driver_details.date_of_birth || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('nif')}
            description={report.driver_details.nif || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('address')}
            description={report.driver_details.address || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('licenseNumber')}
            description={report.driver_details.license_number || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
        </View>

        {/* Detalhes da Empresa */}
        {report.company_attributions?.length ? <View style={styles.section}>
          <Text variant="titleLarge" style={{ color: theme.colors.primary }}>{t('companyDetails')}</Text>
          <ReportWorkCompanies attributions={report.company_attributions} />
        </View> : <View style={styles.section}>
          <Text variant="titleLarge" style={{ color: theme.colors.primary, fontSize: 20, fontWeight: '600' }}>
            {t('companyDetails')}
          </Text>
          <Divider style={[styles.divider, { backgroundColor: theme.colors.outline }]} />
          <List.Item
            title={t('companyName')}
            description={report.company_details.name || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={report.company_details.tax_identifier_kind === 'nif' ? t('nif') : t('nipc')}
            description={report.company_details.tax_identifier || report.company_details.nipc || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('address')}
            description={report.company_details.address || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('companyEmail')}
            description={report.company_details.email || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
        </View>}

        {/* Relatório Semanal */}
        <View style={styles.section}>
          <Text variant="titleLarge" style={{ color: theme.colors.primary, fontSize: 20, fontWeight: '600' }}>
            {t('weeklyDrivingReport')}
          </Text>
          <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant, fontSize: 14, marginTop: 4 }}>
            {`${report.week_start} - ${report.week_end}`}
          </Text>
          <Divider style={[styles.divider, { backgroundColor: theme.colors.outline }]} />
          {Object.entries(report.daily_hours).map(([date, data]) => (
            <List.Accordion
              key={date}
              title={`${date}: ${data.total_driving_time.hours}h ${data.total_driving_time.minutes}m`}
              titleStyle={{ color: theme.colors.onSurface, fontSize: 16, fontWeight: '500' }}
              expanded={expandedDays[date]}
              onPress={() => toggleDay(date)}
              style={[styles.accordion, { backgroundColor: theme.colors.surfaceVariant }]}
            >
              <List.Item
                title={t('vehicleRegistration')}
                description={data.vehicle_reg || t('notProvided')}
                titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
                descriptionStyle={{ color: theme.colors.onSurface }}
              />
              <List.Item
                title={t('odometerStart')}
                description={data.odometer_start !== null ? `${data.odometer_start} km` : t('notProvided')}
                titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
                descriptionStyle={{ color: theme.colors.onSurface }}
                right={isReportRecordEditingEnabled() ? () => (
                  <Button compact onPress={() => setEditDate(date)} icon="pencil">
                    {t('edit')}
                  </Button>
                ) : undefined}
              />
              <List.Item
                title={t('odometerEnd')}
                description={data.odometer_end !== null ? `${data.odometer_end} km` : t('notProvided')}
                titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
                descriptionStyle={{ color: theme.colors.onSurface }}
                right={() => (editedByDate[date]?.sessionStartEdited || editedByDate[date]?.sessionEndEdited) ? (
                  <Chip compact>{t('edited', { defaultValue: 'Edited' })}</Chip>
                ) : undefined}
              />
              <List.Item
                title={t('drivingTime')}
                description={`${data.total_driving_time.hours}h ${data.total_driving_time.minutes}m`}
                titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
                descriptionStyle={{ color: theme.colors.onSurface }}
              />
              <List.Item
                title={t('availabilityTime')}
                description={`${data.total_availability_time.hours}h ${data.total_availability_time.minutes}m`}
                titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
                descriptionStyle={{ color: theme.colors.onSurface }}
              />
              <List.Item
                title={t('restTime')}
                description={`${data.total_rest_time.hours}h ${data.total_rest_time.minutes}m`}
                titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
                descriptionStyle={{ color: theme.colors.onSurface }}
              />
              {data.events.length > 0 && (
                <Surface elevation={1} style={[styles.tableContainer, { backgroundColor: theme.colors.surface }]}> 
                  <DataTable>
                    <DataTable.Header style={{ backgroundColor: theme.colors.surfaceVariant }}>
                      <DataTable.Title textStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '600' }}>
                        {t('eventType')}
                      </DataTable.Title>
                      <DataTable.Title textStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '600' }}>
                        {t('start')}
                      </DataTable.Title>
                      <DataTable.Title textStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '600' }}>
                        {t('end')}
                      </DataTable.Title>
                      <DataTable.Title numeric textStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '600' }}>
                        {t('total')}
                      </DataTable.Title>
                    </DataTable.Header>
                    {data.events.map((event, index) => (
                      <DataTable.Row key={index} style={[styles.tableRow, { borderBottomColor: theme.colors.outline }]}> 
                        <DataTable.Cell textStyle={{ color: theme.colors.onSurface }}>
                          {event.event_type === 'driving' ? t('driving') : t('available')}
                          {event.company_name ? ` — ${event.company_name}` : ''}
                        </DataTable.Cell>
                        <DataTable.Cell textStyle={{ color: theme.colors.onSurface }}>
                          <View style={styles.timeLocationContainer}>
                            <Text style={{ color: theme.colors.onSurface }}>{event.start}</Text>
                            {event.start_location && (
                              <IconButton
                                icon="map-marker"
                                size={20}
                                onPress={() => openGoogleMaps(event.start_location)}
                                iconColor={theme.colors.primary}
                              />
                            )}
                          </View>
                        </DataTable.Cell>
                        <DataTable.Cell textStyle={{ color: theme.colors.onSurface }}>
                          <View style={styles.timeLocationContainer}>
                            <Text style={{ color: theme.colors.onSurface }}>{event.end}</Text>
                            {event.end_location && (
                              <IconButton
                                icon="map-marker"
                                size={20}
                                onPress={() => openGoogleMaps(event.end_location)}
                                iconColor={theme.colors.primary}
                              />
                            )}
                          </View>
                        </DataTable.Cell>
                        <DataTable.Cell numeric textStyle={{ color: theme.colors.onSurface }}>
                          {`${Math.floor(event.total / 60)}h ${event.total % 60}m`}
                        </DataTable.Cell>
                      </DataTable.Row>
                    ))}
                  </DataTable>
                </Surface>
              )}
              {data.observations.length > 0 && (
                <>
                  <List.Subheader style={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}>
                    {t('observations')}
                  </List.Subheader>
                  {data.observations.map((obs, index) => (
                    <List.Item
                      key={index}
                      title={obs}
                      titleStyle={{ color: theme.colors.onSurface }}
                      titleNumberOfLines={0}
                    />
                  ))}
                </>
              )}
            </List.Accordion>
          ))}
          <Divider style={[styles.divider, { backgroundColor: theme.colors.outline }]} />
          <Text variant="bodyLarge" style={{ color: theme.colors.onSurface, marginTop: 8, fontWeight: '600' }}>
            {t('totalWeekDriving')}: {report.total_week.driving.hours}h {report.total_week.driving.minutes}m
          </Text>
          <Text variant="bodyLarge" style={{ color: theme.colors.onSurface, marginTop: 8, fontWeight: '600' }}>
            {t('totalWeekAvailability')}: {report.total_week.availability.hours}h {report.total_week.availability.minutes}m
          </Text>
          <Text variant="bodyLarge" style={{ color: theme.colors.onSurface, marginTop: 8, fontWeight: '600' }}>
            {t('totalWeekRest')}: {report.total_week.rest.hours}h {report.total_week.rest.minutes}m
          </Text>
        </View>
      </ScrollView>

      <FAB
        icon="file-download"
        label={t('exportPDF')}
        style={[styles.fabExport, Platform.OS === 'web' && styles.fabExportWeb]}
        onPress={exportToPDF}
        mode="elevated"
      />

      {editDate && (
        <EditDayEventsModal
          visible={!!editDate}
          date={editDate}
          onDismiss={() => setEditDate(null)}
          onSaved={() => {
            // Ensure modal is closed and refetch report
            setEditDate(null);
            fetchReport(report.week_start);
          }}
        />
      )}

      <FAB
        icon="calendar"
        label={t('selectWeek')}
        style={[styles.fabGroup, Platform.OS === 'web' && styles.fabGroupWeb]}
        onPress={() => {
          setWeekPickerVisible(true);
        }}
        mode="elevated"
      />

      <WeekPickerModal
        visible={weekPickerVisible}
        onDismiss={() => setWeekPickerVisible(false)}
        onSelectWeek={handleWeekSelection}
        currentWeekStart={report.week_start}
      />
    </Surface>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    paddingBottom: 100, // Space for FABs
  },
  section: {
    marginBottom: 24,
  },
  divider: {
    marginVertical: 12,
  },
  accordion: {
    borderRadius: 8,
    marginVertical: 4,
  },
  tableContainer: {
    marginVertical: 8,
    borderRadius: 8,
  },
  tableRow: {
    borderBottomWidth: 1,
  },
  timeLocationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  fabGroup: {
    position: 'absolute',
    bottom: 16,
    right: 16,
  },
  fabGroupWeb: {
    bottom: 32,
    right: 32,
  },
  fabExport: {
    position: 'absolute',
    bottom: 32,
    left: 16,
  },
  fabExportWeb: {
    bottom: 48,
    left: 32,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});